import * as preact from "preact";

import * as modalController from "modalController";
import * as readerPageNavigation from "readerPageNavigation";
import readlangClient from "readlangClient";
import { cn } from "shared/utils";
import ReaderViewStore from "stores/ReaderViewStore";
import stores from "stores/stores";

interface Match {
  position: number;
  context: string;
  longContext: string;
}

interface Props {
  appRouter: any;
  readerViewStore: ReaderViewStore;
}

interface State {
  query: string;
  searchedQuery: string;
  matches: Match[];
  totalWords: number;
  hasMore: boolean;
  loading: boolean;
  expandedIndex: number | null;
  showSpoilers: boolean;
  error: string | null;
  hasSearched: boolean;
}

class FindInTextModal extends preact.Component<Props, State> {
  private inputRef: HTMLInputElement | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      query: "",
      searchedQuery: "",
      matches: [],
      totalWords: 0,
      hasMore: false,
      loading: false,
      expandedIndex: null,
      showSpoilers: false,
      error: null,
      hasSearched: false,
    };
  }

  focusInput = () => {
    this.inputRef?.focus();
  };

  getCurrentPosition = (): number => {
    return this.props.readerViewStore.fromPosition.value || 0;
  };

  handleSearch = async () => {
    const { query } = this.state;
    if (!query.trim()) return;

    this.setState({ loading: true, error: null, expandedIndex: null });

    try {
      const { data } = await readlangClient.request(
        "GET",
        `/api/book/${stores.bookModel.id}/search`,
        { query: query.trim() }
      );
      this.setState({
        searchedQuery: query.trim(),
        matches: data.matches,
        totalWords: data.totalWords,
        hasMore: data.hasMore,
        loading: false,
        hasSearched: true,
      });
    } catch (error: any) {
      this.setState({
        error: error.jqXHR?.alreadyNotified
          ? "Connection problem. Please try again."
          : "Search failed. Please try again.",
        loading: false,
        hasSearched: true,
      });
    }
  };

  handleGoToPosition = (position: number) => {
    const userBook = stores.bookModel.book.value.userBook;
    const readTo = Number(userBook?.readTo ?? 0);

    if (position > readTo) {
      const totalWords = Number(stores.bookModel.book.value.totalWords || 1);
      const currentPercent = Math.round((readTo / totalWords) * 100);
      const targetPercent = Math.round((position / totalWords) * 100);
      const confirmed = confirm(
        `This will update your furthest read position from ${currentPercent}% to ${targetPercent}%. ` +
          `You won't be able to return to your current position using "Go to furthest read location". Continue?`
      );
      if (!confirmed) return;
    }

    modalController.cancel();
    readerPageNavigation.goToLocation("from", position, this.props.appRouter);
    // Store the position for highlight flash
    (window as any).__pendingSearchHighlight = position;
  };

  highlightMatch = (text: string, query: string): preact.ComponentChildren => {
    const stripAccents = (str: string) =>
      str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const queryNormalized = stripAccents(query.trim().toLowerCase());
    if (!queryNormalized) return text;

    // Build mapping from accent-stripped index to original text index.
    // NFD decomposes "é" into "e" + combining accent; stripping the accent
    // makes the stripped string shorter, so indices don't correspond 1:1.
    const textLower = text.toLowerCase();
    const strippedToOrig: number[] = [];
    for (let index = 0; index < textLower.length; index++) {
      const nfd = textLower[index].normalize("NFD");
      for (let j = 0; j < nfd.length; j++) {
        const code = nfd.charCodeAt(j);
        if (!(code >= 0x0300 && code <= 0x036f)) {
          strippedToOrig.push(index);
        }
      }
    }
    strippedToOrig.push(text.length); // sentinel for end-of-string

    const textStripped = stripAccents(textLower);
    const result: preact.ComponentChildren[] = [];
    let lastOrigIndex = 0;

    let searchFrom = 0;
    while (searchFrom < textStripped.length) {
      const matchIndex = textStripped.indexOf(queryNormalized, searchFrom);
      if (matchIndex === -1) break;

      const origStart = strippedToOrig[matchIndex];
      const origEnd = strippedToOrig[matchIndex + queryNormalized.length];

      if (origStart > lastOrigIndex) {
        result.push(text.slice(lastOrigIndex, origStart));
      }
      result.push(
        <strong class="findInTextHighlight" key={matchIndex}>
          {text.slice(origStart, origEnd)}
        </strong>
      );
      lastOrigIndex = origEnd;
      searchFrom = matchIndex + queryNormalized.length;
    }

    if (lastOrigIndex < text.length) {
      result.push(text.slice(lastOrigIndex));
    }

    return result;
  };

  render() {
    const {
      query,
      searchedQuery,
      matches,
      totalWords,
      hasMore,
      loading,
      expandedIndex,
      showSpoilers,
      error,
      hasSearched,
    } = this.state;
    const currentPosition = this.getCurrentPosition();
    const hasSpoilers =
      !showSpoilers &&
      matches.some((match) => match.position > currentPosition);

    // Split matches into before and after current position
    const beforeMatches: { match: Match; originalIndex: number }[] = [];
    const afterMatches: { match: Match; originalIndex: number }[] = [];
    for (let index = 0; index < matches.length; index++) {
      if (matches[index].position <= currentPosition) {
        beforeMatches.push({ match: matches[index], originalIndex: index });
      } else {
        afterMatches.push({ match: matches[index], originalIndex: index });
      }
    }

    const resultCountText =
      matches.length === 0
        ? "No results found"
        : hasMore
          ? `${matches.length}+ results found`
          : `${matches.length} result${matches.length === 1 ? "" : "s"} found`;

    return (
      <div class="findInTextModal">
        <div class="findInTextScrollable">
          <div class="findInTextSearchRow">
            <input
              class="findInTextInput"
              type="text"
              placeholder="Search for a word or phrase..."
              value={query}
              ref={(ref) => {
                this.inputRef = ref;
              }}
              onInput={(event) =>
                this.setState({
                  query: (event.target as HTMLInputElement).value,
                })
              }
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  this.handleSearch();
                }
              }}
            />
            <button
              class="findInTextSearchButton"
              onClick={this.handleSearch}
              disabled={loading || !query.trim()}
            >
              {loading ? "Searching..." : "Search"}
            </button>
          </div>

          {error && <p class="findInTextError">{error}</p>}

          {hasSearched && !loading && !error && (
            <p class="findInTextResultCount">{resultCountText}</p>
          )}

          <div class="findInTextResults">
            {beforeMatches.map(({ match, originalIndex }) => {
              const percentage = totalWords
                ? Math.round((match.position / totalWords) * 100)
                : 0;
              const isExpanded = expandedIndex === originalIndex;

              return (
                <div
                  class={cn({
                    findInTextResult: true,
                    expanded: isExpanded,
                  })}
                  key={originalIndex}
                  onClick={() =>
                    this.setState({
                      expandedIndex: isExpanded ? null : originalIndex,
                    })
                  }
                >
                  <div class="findInTextResultHeader">
                    <span class="findInTextPosition">
                      Position {match.position.toLocaleString("en")} (
                      {percentage}%)
                    </span>
                  </div>
                  <div class="findInTextContext">
                    {this.highlightMatch(
                      isExpanded ? match.longContext : match.context,
                      searchedQuery
                    )}
                  </div>
                  {isExpanded && (
                    <button
                      class="findInTextReadFromHere"
                      onClick={(event) => {
                        event.stopPropagation();
                        this.handleGoToPosition(match.position);
                      }}
                    >
                      Start reading from this position
                    </button>
                  )}
                </div>
              );
            })}

            {matches.length > 0 && (
              <div class="findInTextDivider">
                <span>
                  You are here (position {currentPosition.toLocaleString("en")})
                </span>
              </div>
            )}

            {afterMatches.map(({ match, originalIndex }) => {
              const percentage = totalWords
                ? Math.round((match.position / totalWords) * 100)
                : 0;
              const isExpanded = expandedIndex === originalIndex;
              const isSpoilerHidden = !showSpoilers;

              return (
                <div
                  class={cn({
                    findInTextResult: true,
                    expanded: isExpanded,
                    spoilerHidden: isSpoilerHidden,
                  })}
                  key={originalIndex}
                  onClick={
                    isSpoilerHidden
                      ? undefined
                      : () =>
                          this.setState({
                            expandedIndex: isExpanded ? null : originalIndex,
                          })
                  }
                >
                  <div class="findInTextResultHeader">
                    <span class="findInTextPosition">
                      Position {match.position.toLocaleString("en")} (
                      {percentage}%)
                    </span>
                  </div>
                  <div
                    class={cn({
                      findInTextContext: true,
                      findInTextSpoiler: isSpoilerHidden,
                    })}
                  >
                    {isSpoilerHidden
                      ? "Content hidden \u2014 may contain spoilers"
                      : this.highlightMatch(
                          isExpanded ? match.longContext : match.context,
                          searchedQuery
                        )}
                  </div>
                  {isExpanded && !isSpoilerHidden && (
                    <button
                      class="findInTextReadFromHere"
                      onClick={(event) => {
                        event.stopPropagation();
                        this.handleGoToPosition(match.position);
                      }}
                    >
                      Start reading from this position
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {hasSpoilers && (
          <div class="findInTextStickyBar">
            <button onClick={() => this.setState({ showSpoilers: true })}>
              Show spoilers ({afterMatches.length} result
              {afterMatches.length === 1 ? "" : "s"} hidden)
            </button>
          </div>
        )}
      </div>
    );
  }
}

export default FindInTextModal;
